import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, TOOLTIP_POSITION } from 'taboola-ultimate-ui';
import { TooltipWrapper } from 'components/TooltipWrapper/TooltipWrapper';
import { useNavigate } from 'hooks';
import { CAMPAIGNS_ROUTE } from 'modules/campaigns/config';
import { CAMPAIGN_REPORT_ID } from 'modules/campaigns/modules/campaigns-reports/config/reports/campaign/campaignReportId';
import { REPORT_ID, SEARCH_TERM } from 'modules/campaigns/modules/campaigns-reports/hooks';
import { REPORT_FILTERS } from 'modules/campaigns/modules/campaigns-reports/hooks/usePermanentReportFilters';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { mergeQueryParams } from 'modules/taboola-common-frontend-modules/query-params';
import FormattedMessageCellRenderer from './FormattedMessageCellRenderer';
import styles from './assignmentCellRenderer.module.scss';

const TooltipContent = ({ adGroups, onCampaignClick }) => {
    if (!adGroups?.length) {
        return (
            <FormattedMessage
                id="app.campaigns.reports.grid.column.content.adGroups.empty"
                defaultMessage="No campaigns assigned"
            />
        );
    }
    return (
        <div>
            <FormattedMessage
                id="app.campaigns.reports.grid.column.content.adGroups.title"
                defaultMessage="Assigned campaigns:"
            />
            <br />
            {adGroups.map(adGroup => (
                <div key={adGroup.campaignId}>
                    <Button onClick={() => onCampaignClick(adGroup.campaignId)} className={styles['campaign-link']}>
                        {adGroup.campaignId}
                    </Button>
                </div>
            ))}
        </div>
    );
};

export const AssignmentCellRenderer = ({ data = {} }) => {
    const { adGroups } = data;
    const navigate = useNavigate();
    const { accountName } = useAccount();

    const handleCampaignClick = useCallback(
        campaignId => {
            const campaignPath = `${CAMPAIGNS_ROUTE}${mergeQueryParams('', {
                [REPORT_ID]: CAMPAIGN_REPORT_ID,
                [SEARCH_TERM]: campaignId,
                [REPORT_FILTERS]: [],
                autoFiltersTrigger: encodeURIComponent(JSON.stringify({ reportId: CAMPAIGN_REPORT_ID, accountName })),
            })}`;
            navigate(campaignPath);
        },
        [navigate, accountName]
    );

    const tooltipContent = TooltipContent({ adGroups, onCampaignClick: handleCampaignClick });
    const campaignsCount = adGroups?.length || 0;

    return (
        <TooltipWrapper
            tooltipContent={tooltipContent}
            tooltipOptions={{
                arrow: true,
                interactive: true,
                position: TOOLTIP_POSITION.TOP,
            }}
        >
            <div className={styles['container']}>
                <span className={styles['campaigns-count']}>{campaignsCount}</span>
                <FormattedMessageCellRenderer
                    value="app.campaigns.creative-library.assignment.campaigns"
                    defaultValue="campaigns"
                    isSearchable={true}
                />
            </div>
        </TooltipWrapper>
    );
};

AssignmentCellRenderer.propTypes = {
    data: PropTypes.shape({
        adGroups: PropTypes.arrayOf(
            PropTypes.shape({
                campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            })
        ),
    }),
};
